import { v } from "convex/values"

export const DOCUMENT_KINDS = [
  "arbitration_agreement",
  "bill_of_sale",
  "buyers_order",
  "credit_disclosure",
  "delivery_checklist",
  "gap_waiver",
  "goodwill_repair_authorization",
  "insurance_verification",
  "loan_application",
  "odometer_statement",
  "other",
  "payoff_authorization",
  "power_of_attorney",
  "privacy_notice",
  "registration_application",
  "repair_authorization",
  "retail_installment_sale_contract",
  "service_contract",
  "title_application",
  "trade_in_appraisal",
  "warranty_disclaimer",
] as const

export type DocumentKind = (typeof DOCUMENT_KINDS)[number]

export type DocumentCategory = "sales" | "finance" | "service" | "trade-in" | "other"

export const documentKindValidator = v.union(
  v.literal("arbitration_agreement"),
  v.literal("bill_of_sale"),
  v.literal("buyers_order"),
  v.literal("credit_disclosure"),
  v.literal("delivery_checklist"),
  v.literal("gap_waiver"),
  v.literal("goodwill_repair_authorization"),
  v.literal("insurance_verification"),
  v.literal("loan_application"),
  v.literal("odometer_statement"),
  v.literal("other"),
  v.literal("payoff_authorization"),
  v.literal("power_of_attorney"),
  v.literal("privacy_notice"),
  v.literal("registration_application"),
  v.literal("repair_authorization"),
  v.literal("retail_installment_sale_contract"),
  v.literal("service_contract"),
  v.literal("title_application"),
  v.literal("trade_in_appraisal"),
  v.literal("warranty_disclaimer"),
)

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  bill_of_sale: "Bill of Sale",
  buyers_order: "Buyer's Order",
  retail_installment_sale_contract: "Retail Installment Sale Contract",
  loan_application: "Loan Application",
  credit_disclosure: "Credit Disclosure",
  privacy_notice: "Privacy Notice",
  arbitration_agreement: "Arbitration Agreement",
  warranty_disclaimer: "Warranty Disclaimer",
  service_contract: "Service Contract",
  gap_waiver: "GAP Waiver",
  power_of_attorney: "Power of Attorney",
  odometer_statement: "Odometer Statement",
  title_application: "Title Application",
  registration_application: "Registration Application",
  insurance_verification: "Insurance Verification",
  trade_in_appraisal: "Trade-In Appraisal",
  payoff_authorization: "Payoff Authorization",
  delivery_checklist: "Delivery Checklist",
  repair_authorization: "Repair Authorization",
  goodwill_repair_authorization: "Goodwill Repair Authorization",
  other: "Other",
}

export const DOCUMENT_KIND_DEFAULT_CATEGORIES: Record<DocumentKind, DocumentCategory> = {
  bill_of_sale: "sales",
  buyers_order: "sales",
  retail_installment_sale_contract: "finance",
  loan_application: "finance",
  credit_disclosure: "finance",
  privacy_notice: "finance",
  arbitration_agreement: "finance",
  warranty_disclaimer: "sales",
  service_contract: "finance",
  gap_waiver: "finance",
  power_of_attorney: "sales",
  odometer_statement: "sales",
  title_application: "sales",
  registration_application: "sales",
  insurance_verification: "finance",
  trade_in_appraisal: "trade-in",
  payoff_authorization: "trade-in",
  delivery_checklist: "sales",
  repair_authorization: "service",
  goodwill_repair_authorization: "service",
  other: "other",
}

export const US_STATE_OPTIONS = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
] as const

export const US_STATE_LABELS = Object.fromEntries(
  US_STATE_OPTIONS.map((state) => [state.code, state.name]),
) as Record<(typeof US_STATE_OPTIONS)[number]["code"], (typeof US_STATE_OPTIONS)[number]["name"]>
